"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUrlParams } from "@/hooks/use-url-params";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
	BarChart3,
	FileText,
	LogOut,
	Lock,
	GraduationCap,
	RefreshCw,
	Download,
	Loader2,
} from "lucide-react";
import { useStatisticsEvents } from "@/hooks/useEventStream";
import statisticsAPIService, {
	type OverallStatistics,
	type StatisticsByDepartment,
	type StatisticsByApprover,
	type StatisticsByAdmissionMethod,
	type StatisticsByFaculty,
	type GenderDistribution,
	type StatisticsFilter,
} from "@/lib/statistics-api.service";
import { useAuth } from "@/global-hooks/useAuth";
import { ChangePasswordModal } from "@/components/change-password-modal";
import { ModeToggle } from "@/components/mode-toggle";
import {
	OverviewTab,
	MajorsTab,
	ProvincesTab,
	HighSchoolsTab,
	ApproversTab,
	AdmissionTab,
	OtherStatisticsTab,
	QuotaTab,
	PacingTab,
	HocBaTab,
	CombinationLookupTab,
	type StatisticsFilters,
	type StatisticsFilterType,
} from "@/components/statistics-tabs";
import { toAPIFilter } from "@/components/statistics-tabs/utils";
import { FacultyFilter } from "@/components/statistics-tabs/FacultyFilter";
import { CohortFilter, DEFAULT_ADMISSION_YEAR } from "@/components/statistics-tabs/CohortFilter";
import { Toast } from "@/lib/utils";
import { hasPermission } from "@/lib/permissions";

/** Khoa đang chọn, giữ lại giữa các lần vào trang. */
const FACULTY_FILTER_KEY = "statistics.facultyCode";

/**
 * Tên tham số trên URL. Ngắn và ổn định: link được dán vào chat, vào email, vào
 * báo cáo — đổi tên sau này là làm chết những link đã gửi đi.
 */
const PARAM = { tab: "tab", section: "sub", year: "year", faculty: "khoa" } as const;

/** Mọi tab hợp lệ của trang. Tham số lạ sẽ lùi về phần tử đầu tiên. */
const TABS = [
	"overview",
	"majors",
	"quota",
	"faculties",
	"provinces",
	"high-schools",
	"approvers",
	"admission",
	"pacing",
	"hoc-ba",
	"combinations",
	"other",
] as const;

type TabId = (typeof TABS)[number];

/** URL do người dùng gõ hoặc sửa tay, nên tham số lạ phải rơi về tab mặc định. */
function isKnownTab(value: string | null): value is TabId {
	return value !== null && (TABS as readonly string[]).includes(value);
}

interface StatisticsPageProps {
	className?: string;
}

export default function StatisticsPage({ className }: StatisticsPageProps) {
	const { user } = useAuth();
	// Quyền xem Nhịp độ: admin đi qua vô điều kiện, còn lại phải được cấp riêng.
	// Đây CHỈ là lớp hiển thị — endpoint `/statistics/reception-timeline` cũng
	// đã bị chặn ở server, ẩn tab một mình chỉ là giấu đường đi.
	const canViewPacing = user?.role === "admin" || hasPermission(user?.permissions, "view_pacing");
	const auth = useAuth();
	const navigate = useNavigate();
	const [showChangePassword, setShowChangePassword] = useState(false);
	const [overallStats, setOverallStats] = useState<OverallStatistics | null>(null);
	const [departmentStats, setDepartmentStats] = useState<StatisticsByDepartment[]>([]);
	const [facultyStats, setFacultyStats] = useState<StatisticsByFaculty[]>([]);
	const [approverStats, setApproverStats] = useState<StatisticsByApprover[]>([]);
	const [admissionStats, setAdmissionStats] = useState<StatisticsByAdmissionMethod[]>([]);
	const [_genderStats, setGenderStats] = useState<GenderDistribution | null>(null);
	// Hai trạng thái tải, không phải một. Lần đầu chưa có gì để nhìn nên chiếm cả
	// trang; những lần sau đã có số liệu trên màn hình, và thay chúng bằng màn hình
	// tải sẽ unmount cả cây tab — người đọc mất tab đang xem lẫn vị trí cuộn.
	const [isInitialLoading, setIsInitialLoading] = useState(true);
	// Nguồn của lượt nạp lại, chứ không chỉ "có đang nạp hay không".
	//
	// "user" là người dùng bấm Tải lại hoặc đổi bộ lọc: họ vừa làm một thao tác và
	// đang chờ, nên vùng số liệu mờ đi là câu trả lời đúng.
	//
	// "live" là luồng sự kiện — NGƯỜI KHÁC vừa đổi dữ liệu. Người đang ngồi đọc
	// không làm gì cả, và làm mờ cả vùng nội dung dưới chân họ mỗi lần có bàn tiếp
	// nhận bấm nút thì trang giống như tự F5. Nhánh này chỉ hiện một chỉ báo nhỏ
	// trên thanh công cụ; số liệu được thay tại chỗ khi về.
	const [refreshSource, setRefreshSource] = useState<"user" | "live" | null>(null);
	const isRefreshing = refreshSource !== null;
	const [error, setError] = useState<string | null>(null);
	// Ref chứ không phải state: đây là "đã từng tải xong chưa", chỉ dùng để chọn
	// nhánh bên trong lần fetch kế tiếp. Là state thì nó thành dependency của
	// `fetchStatistics` và mỗi lần tải xong lại kích hoạt một lần tải nữa.
	const hasLoadedOnce = useRef(false);

	/**
	 * Số thứ tự của lần nạp gần nhất, để bỏ qua response đã bị thay thế.
	 *
	 * Debounce gộp được các lần đổi bộ lọc liên tiếp, nhưng không huỷ được một
	 * request đã xuất phát. Nên nếu không có mốc này thì response về SAU CÙNG
	 * thắng, chứ không phải request MỚI NHẤT:
	 *
	 *   chọn CNTT  -> request 1 gửi đi, mạng chậm
	 *   đổi sang HH -> request 2 gửi đi, về trước
	 *   request 2 về -> trang hiện số HH
	 *   request 1 về -> trang hiện số CNTT, còn dropdown vẫn ghi HH
	 *
	 * Tài khoản bị giới hạn khoa không bị ảnh hưởng — server lấy phạm vi từ phiên
	 * chứ không từ tham số client gửi lên. Nhưng tài khoản không giới hạn tự thu
	 * hẹp bằng dropdown thì thấy số của khoa này dưới nhãn của khoa kia.
	 */
	const latestFetchId = useRef(0);
	// Tăng mỗi lần người dùng bấm Tải lại. Các tab tự gọi API riêng lấy đây làm
	// tín hiệu — nếu không, "Tải lại" sẽ không làm mới đúng tab đang mở.
	const [refreshToken, setRefreshToken] = useState(0);
	// URL là nguồn sự thật cho tab, mục con, khoá và khoa: mỗi màn hình phải có
	// một link đi thẳng tới nó. Controlled cũng vẫn cần thiết vì mỗi lần đổi bộ
	// lọc trang rơi vào nhánh tải và unmount cả cây tab — tabs không controlled
	// sẽ quay về "Tổng quan" và ném người đọc khỏi tab họ đang xem.
	const [urlParams, setUrlParams] = useUrlParams();
	const tabParam = urlParams.get(PARAM.tab);
	const requestedTab = isKnownTab(tabParam) ? tabParam : TABS[0];
	const activeSection = urlParams.get(PARAM.section);
	// Khoa lùi về localStorage khi URL không nói gì: người dùng thường rời sang
	// dashboard tra một sinh viên rồi quay lại, mất lựa chọn mỗi lần như vậy thì
	// họ phải chọn lại liên tục. URL có thì URL thắng — link được gửi đi phải mở
	// ra đúng cái người gửi đang nhìn, không phải khoa của người nhận.
	const facultyCode =
		urlParams.get(PARAM.faculty) ?? localStorage.getItem(FACULTY_FILTER_KEY) ?? undefined;
	// Khoá luôn có giá trị ngay từ lần render đầu: nếu để trống rồi mới điền,
	// lần fetch đầu chạy không lọc và người dùng thấy số của mọi khoá nhấp một
	// cái trước khi nó đổi sang khoá mặc định.
	const admissionYear = urlParams.get(PARAM.year) ?? DEFAULT_ADMISSION_YEAR;

	// Đã thu hẹp về một khoa thì bảng "theo khoa" chỉ còn đúng một dòng — nó không
	// nói thêm được gì mà vẫn chiếm một ô trong dải tab. Ẩn hẳn, không để lại chỗ
	// trống: số cột của lưới đổi theo số tab đang hiện.
	//
	// Tài khoản bị ghim khoa (`pinnedCode` trong `FacultyFilter`) luôn có
	// `facultyCode`, nên với họ tab này không bao giờ xuất hiện. Đó là đúng: phạm
	// vi của họ vốn đã là một khoa.
	const showFacultiesTab = !facultyCode;

	// Người dùng có thể đang đứng ở tab "Khoa" rồi mới chọn khoa. Nếu để `value`
	// trỏ vào một tab không còn trigger lẫn content, Radix không khớp được gì và
	// vùng nội dung trắng trơn — nên đẩy về tab đầu ngay tại chỗ dẫn xuất, chứ
	// không bằng một effect chạy sau khi đã render một khung rỗng.
	const activeTab = requestedTab === "faculties" && !showFacultiesTab ? TABS[0] : requestedTab;

	// Khoảng ngày và lịch hẹn KHÔNG lên URL: chúng là lựa chọn nhất thời của một
	// lần xem, còn khoá và khoa là "tôi đang xem phần nào của đợt tuyển sinh nào"
	// — thứ đáng gửi cho người khác.
	const [transientFilters, setTransientFilters] = useState<StatisticsFilters>({});

	const filters = useMemo<StatisticsFilters>(
		() => ({
			...transientFilters,
			admissionYear,
			facultyCode,
			// Khoá 2026 không dùng lịch hẹn và control đó bị ẩn. Giữ lại giá trị đã
			// chọn từ khoá cũ sẽ tạo ra một bộ lọc đang hoạt động mà không hiển thị ở
			// đâu cả — người dùng thấy số bị thu hẹp và không có cách nào biết vì sao.
			appointmentSlot: admissionYear === "2026" ? undefined : transientFilters.appointmentSlot,
		}),
		[transientFilters, admissionYear, facultyCode],
	);
	const [debouncedFilters, setDebouncedFilters] = useState<StatisticsFilters>(filters);

	// Link cũ `?tab=majors&sub=advanced` từng trỏ tới tab con "Nâng cao"; giờ nó
	// là một tab riêng. Dịch sang địa chỉ mới thay vì để link đã gửi đi mở ra
	// nhầm bảng. `replace` để nút Back không kẹt giữa hai dạng địa chỉ.
	useEffect(() => {
		if (tabParam === "majors" && activeSection === "advanced") {
			setUrlParams({ [PARAM.tab]: "faculties", [PARAM.section]: undefined }, { replace: true });
		}
	}, [tabParam, activeSection, setUrlParams]);

	// SSE integration for real-time updates
	const { shouldRefresh, clearRefreshFlag } = useStatisticsEvents();

	// Debounce date filters to prevent excessive API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedFilters(filters);
		}, 500);

		return () => clearTimeout(timer);
	}, [filters]);

	// Vẫn lưu khoa vào localStorage bên cạnh URL: URL phục vụ link gửi đi, còn
	// localStorage phục vụ người dùng tự mở lại trang bằng bookmark trần.
	// `replace`: đổi bộ lọc không phải là điều hướng, nút Back không nên bắt
	// người dùng lội ngược qua từng lần chỉnh khoa.
	const handleFacultyChange = (code: string | undefined) => {
		if (code) localStorage.setItem(FACULTY_FILTER_KEY, code);
		else localStorage.removeItem(FACULTY_FILTER_KEY);
		setUrlParams({ [PARAM.faculty]: code }, { replace: true });
	};

	// Không lưu localStorage: khoá là "đợt tuyển sinh đang chạy", không phải lựa
	// chọn cá nhân như khoa, và ghim nó lại sẽ khiến người dùng xem số của khoá
	// cũ sau khi đợt mới bắt đầu mà không hiểu vì sao.
	const handleCohortChange = (year: string | undefined) => {
		setUrlParams({ [PARAM.year]: year }, { replace: true });
	};

	// Đổi tab LÀ điều hướng, nên vào lịch sử: Back đưa người đọc về tab trước.
	// Xoá `sub` cùng lúc trong một lần ghi — mục con chỉ có nghĩa trong tab
	// "Khác", để nó sót lại thì link mang theo một tham số vô nghĩa.
	const handleTabChange = (tab: string) => {
		setUrlParams({ [PARAM.tab]: tab, [PARAM.section]: undefined });
	};

	const handleSectionChange = (section: string | null) => {
		setUrlParams({ [PARAM.section]: section ?? undefined });
	};

	// Các tab con gửi lên nguyên cả object filters. Khoá và khoa thuộc về URL,
	// phần còn lại là state cục bộ — tách ở đây để bên gọi không phải biết mảnh
	// nào sống ở đâu.
	const handleFiltersChange = (next: StatisticsFilters) => {
		const { admissionYear: nextYear, facultyCode: nextFaculty, ...rest } = next;
		setTransientFilters(rest);

		if (nextYear !== admissionYear || nextFaculty !== facultyCode) {
			if (nextFaculty) localStorage.setItem(FACULTY_FILTER_KEY, nextFaculty);
			else localStorage.removeItem(FACULTY_FILTER_KEY);
			setUrlParams({ [PARAM.year]: nextYear, [PARAM.faculty]: nextFaculty }, { replace: true });
		}
	};

	// `logout` reloads onto the login page itself, so there is nothing to
	// navigate to here.
	const handleSignOut = () => auth.logout();

	// Dùng chung `toAPIFilter` với các tab tự fetch, không tự dịch lần thứ hai.
	// Trước đây trang này có bản dịch riêng, và khi thêm bộ lọc khoá thì chỉ một
	// trong hai bản được cập nhật — trang hiện đúng khoá còn tab "Tỉnh thành" /
	// "Trường THPT" vẫn trả số của mọi khoá. Một nguồn dịch duy nhất thì lần tới
	// thêm bộ lọc mới không thể lệch nữa.
	const buildAPIFilter = (type: StatisticsFilterType = "total"): StatisticsFilter =>
		toAPIFilter(debouncedFilters, type);

	// Fetch statistics data
	const fetchStatistics = useCallback(
		async (source: "user" | "live" = "user") => {
			const isFirstLoad = !hasLoadedOnce.current;
			const fetchId = ++latestFetchId.current;
			/** Đã có lần nạp mới hơn xuất phát; kết quả này không còn khớp giao diện. */
			const superseded = () => fetchId !== latestFetchId.current;

			try {
				if (isFirstLoad) setIsInitialLoading(true);
				else setRefreshSource(source);
				const filter = buildAPIFilter();
				// Một request, không phải sáu. Sáu request song song là sáu giao dịch
				// riêng dưới database, tức sáu ảnh chụp ở sáu thời điểm — thẻ tổng quan
				// và tab ngành có thể hiện hai trạng thái khác nhau của cùng một cơ sở
				// dữ liệu mà không có dấu hiệu gì. Gọi một lần thì cả trang cùng một
				// thời điểm, và chiếm một connection thay vì sáu.
				const { overall, departments, faculties, approvers, admissions, gender } =
					await statisticsAPIService.getDashboard(filter);

				if (superseded()) return;

				setOverallStats(overall);
				setDepartmentStats(departments);
				setFacultyStats(faculties);
				setApproverStats(approvers);
				setAdmissionStats(admissions);
				setGenderStats(gender);
				hasLoadedOnce.current = true;
			} catch (err) {
				// Lỗi của một lần nạp đã bị thay thế cũng không được hiện: người dùng
				// vừa đổi bộ lọc và đang chờ kết quả mới, một toast lỗi của bộ lọc cũ
				// chỉ khiến họ tưởng bộ lọc mới hỏng.
				if (superseded()) return;

				console.error("Error fetching statistics:", err);
				const message = err instanceof Error ? err.message : "Failed to load statistics";

				// Chỉ lần đầu mới chiếm cả trang bằng màn hình lỗi: lúc đó không có gì
				// khác để hiện. Về sau, số liệu đang hiển thị vẫn còn giá trị hơn một
				// trang trống, nên báo lỗi bằng toast và giữ nguyên chúng.
				if (isFirstLoad) setError(message);
				else Toast.error("Không tải lại được thống kê", message);
			} finally {
				// `finally` chạy cả sau `return` ở trên, nên phải kiểm lại: tắt spinner
				// ở đây sẽ tắt nhầm spinner của lần nạp mới hơn đang còn chạy, và người
				// dùng thấy giao diện báo xong trong khi số vẫn chưa về.
				if (!superseded()) {
					if (isFirstLoad) setIsInitialLoading(false);
					else setRefreshSource(null);
				}
			}
		},
		[debouncedFilters],
	);

	// File toàn bộ dữ liệu mất vài giây để dựng, nên dùng `Toast.promise`: người
	// dùng thấy nó đang chạy thay vì tưởng nút không ăn.
	const [isExporting, setIsExporting] = useState(false);

	const handleExportExcel = async () => {
		setIsExporting(true);
		// `Toast.promise` chỉ hiển thị, không trả promise, nên nút phải chờ chính
		// lời hứa gốc — chờ nó thì nút nhả ngay lúc vừa bấm.
		// Cả hai bộ lọc, không riêng khoá: file tải về phải khớp đúng màn hình nó
		// được lấy ra từ đó. Truyền thiếu `facultyCode` thì một admin đang xem một
		// khoa vẫn nhận về dữ liệu của mọi khoa mà không có dấu hiệu nào.
		const download = statisticsAPIService.exportAllStudentsAsXLSX({
			admissionYear: filters.admissionYear,
			facultyCode: filters.facultyCode,
		});
		Toast.promise(download, {
			loading: "Đang chuẩn bị file Excel...",
			success: "Đã tải file Excel",
			error: "Không tải được file Excel",
		});

		try {
			await download;
		} catch (err) {
			// Toast ở trên đã báo cho người dùng; ở đây chỉ để lỗi không thành
			// unhandled rejection.
			console.error("Error exporting students XLSX:", err);
		} finally {
			setIsExporting(false);
		}
	};

	// Reload data manually
	const handleReloadData = () => {
		setRefreshToken((token) => token + 1);
		fetchStatistics();
	};

	useEffect(() => {
		fetchStatistics();
	}, [fetchStatistics]); // Re-fetch when debounced date filters change

	// Auto-refresh when the SSE stream indicates data changes
	useEffect(() => {
		if (shouldRefresh) {
			// `refreshToken` vẫn tăng: các tab tự gọi API riêng (tỉnh thành, trường
			// THPT, mục Khác) giữ dữ liệu KHÁC với `getDashboard` của trang, nên bỏ
			// bước này là để chúng hiện số cũ bên cạnh số mới. Chúng tự làm mờ danh
			// sách của mình tại chỗ trong lúc nạp, nên chỉ báo vẫn nằm đúng chỗ dữ
			// liệu đang đổi.
			setRefreshToken((token) => token + 1);
			fetchStatistics("live");
			clearRefreshFlag();
		}
	}, [shouldRefresh, fetchStatistics, clearRefreshFlag]);

	if (isInitialLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center">
					<BarChart3 className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4 animate-pulse" />
					<p className="text-lg text-muted-foreground">Đang tải thống kê...</p>
				</div>
			</div>
		);
	}

	// `error` chỉ được đặt ở lần tải đầu tiên (xem `fetchStatistics`), nên nhánh này
	// không bao giờ nuốt mất một trang đang có số liệu.
	if (error) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center">
					<FileText className="h-12 w-12 text-red-600 dark:text-red-400 mx-auto mb-4" />
					<p className="text-lg text-red-600 dark:text-red-400 mb-2">Lỗi tải thống kê</p>
					<p className="text-sm text-muted-foreground">{error}</p>
				</div>
			</div>
		);
	}

	return (
		<div className={`container mx-auto p-4 md:p-6 space-y-4 md:space-y-6 ${className}`}>
			{/*
			 * Header hai hàng, tách theo vai trò thay vì dồn mọi nút vào một dải.
			 * Hàng trên trả lời "tôi là ai, đi đâu"; hàng dưới trả lời "tôi đang xem
			 * dữ liệu nào". Gộp chung như trước thì bộ lọc khoa nằm lẫn giữa Đăng xuất
			 * và Đổi mật khẩu, còn khối tên người dùng chen vào giữa cắt hàng làm đôi.
			 *
			 * Mọi control ở cả hai hàng đều `size="sm"` / `h-8` — một hàng một chiều
			 * cao (CLAUDE.md §5).
			 */}
			<div className="space-y-3">
				<div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
					{/* `min-w-0` để tiêu đề co lại thay vì đẩy cụm tài khoản ra khỏi mép: flex
			    item mặc định `min-width:auto`, không co dưới bề rộng nội dung. */}
					<div className="min-w-0">
						<h1 className="text-2xl md:text-3xl font-bold text-foreground">Thống kê</h1>
						<p className="text-sm md:text-base text-muted-foreground">
							Thống kê tổng quan về tình trạng tiếp nhận sinh viên
						</p>
					</div>

					<div className="flex flex-wrap items-center gap-2">
						{/* Ẩn dưới sm: ở 390px hai dòng này ăn hết chỗ của hàng nút, mà danh tính
			    không phải thứ cần đọc lại mỗi lần. */}
						<div className="hidden text-right leading-tight sm:block">
							<p className="text-sm font-medium text-foreground">{auth.user?.name}</p>
							<p className="text-xs text-muted-foreground">{auth.user?.MaCB}</p>
						</div>

						<ModeToggle />

						{/* Ba nhánh này phải khớp từng chữ với `RequireDashboardAccess` trong
			    `App.tsx`. Nút và route guard là hai bản chép của cùng một quy tắc: nút
			    chặt hơn guard thì chuyên viên tiếp nhận mất lối vào họ dùng hằng ngày,
			    nút lỏng hơn guard thì bấm vào bị đẩy về `/statistics` — một nút chết. */}
						{(user?.role === "admin" ||
							user?.role === "user" ||
							hasPermission(user?.permissions, "access_dashboard")) && (
							<Button
								onClick={() => navigate("/dashboard")}
								variant="outline"
								size="sm"
								className="flex items-center gap-2 bg-transparent"
							>
								<GraduationCap className="h-4 w-4" />
								<span className="hidden sm:inline">Dashboard</span>
							</Button>
						)}

						<Button
							onClick={() => setShowChangePassword(true)}
							variant="outline"
							size="sm"
							className="flex items-center gap-2"
						>
							<Lock className="h-4 w-4" />
							<span className="hidden sm:inline">Đổi mật khẩu</span>
						</Button>

						<Button
							onClick={handleSignOut}
							variant="outline"
							size="sm"
							className="flex items-center gap-2"
						>
							<LogOut className="h-4 w-4" />
							<span className="hidden sm:inline">Đăng xuất</span>
						</Button>
					</div>
				</div>

				{/* Hàng điều khiển dữ liệu: phạm vi đang xem ở trái, thao tác lên chính dữ
			    liệu đó ở phải. */}
				<div className="flex flex-wrap items-center gap-2">
					<FacultyFilter value={filters.facultyCode} onChange={handleFacultyChange} />
					<CohortFilter value={filters.admissionYear} onChange={handleCohortChange} />

					<div className="flex flex-wrap items-center gap-2 sm:ms-auto">
						{/* Người khác vừa đổi dữ liệu và trang đang lấy số mới. Chỉ báo duy nhất
			    cho nhánh này — không toast (broadcast, xem `useEventStream`), không làm
			    mờ nội dung. */}
						{refreshSource === "live" && (
							<span
								role="status"
								className="flex h-8 items-center gap-1.5 text-xs text-muted-foreground"
							>
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
								<span className="hidden sm:inline">Đang cập nhật</span>
							</span>
						)}

						{/* Khoá theo `refreshSource === "user"` chứ không theo `isRefreshing`: khoá
			    theo cả hai thì mỗi lần một bàn tiếp nhận bấm nút, nút Tải lại của mọi
			    người đang xem lại chớp tắt một cái. */}
						<Button
							onClick={handleReloadData}
							variant="outline"
							size="sm"
							disabled={refreshSource === "user"}
							className="flex items-center gap-2 bg-transparent"
						>
							<RefreshCw className={`h-4 w-4 ${refreshSource === "user" ? "animate-spin" : ""}`} />
							<span className="hidden sm:inline">
								{refreshSource === "user" ? "Đang tải lại..." : "Tải lại"}
							</span>
						</Button>

						{/* Quyền là cờ riêng trên tài khoản, không suy ra từ role: người cần file
			    này thường là ban giám hiệu, còn manager cấp khoa thì không. */}
						{(user?.role === "admin" || hasPermission(user?.permissions, "export_excel")) && (
							<Button
								onClick={handleExportExcel}
								variant="outline"
								size="sm"
								disabled={isExporting}
								className="flex items-center gap-2 bg-transparent"
							>
								{isExporting ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Download className="h-4 w-4" />
								)}
								<span className="hidden sm:inline">
									{isExporting ? "Đang tải..." : "Tải Excel"}
								</span>
							</Button>
						)}
					</div>
				</div>
			</div>

			{/* Statistics Tabs - Mobile Responsive. */}
			<Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
				{/* Mười tab (chín khi ẩn "Khoa") không còn ép vừa một lưới cột cố định —
				    đó là nguyên nhân chữ chồng lên nhau ở 1440px: `grid-cols-N` dùng
				    `minmax(0, 1fr)`, cột không giãn theo nội dung, còn nhãn thì
				    `sm:whitespace-nowrap` không xuống dòng — chữ tràn ra ngoài biên cột
				    và đè lên tab bên cạnh. Thay bằng hàng flex không co (`shrink-0` mỗi
				    tab) + `overflow-x-auto`: hàng dài hơn viewport thì cuộn ngang, không
				    bao giờ chồng chữ, ở bất kỳ bề rộng nào. */}
				<TabsList className="flex w-full items-stretch justify-start gap-2 overflow-x-auto mb-6">
					<TabsTrigger value="overview" className="shrink-0 whitespace-nowrap text-xs sm:text-sm">
						Tổng quan
					</TabsTrigger>
					<TabsTrigger value="majors" className="shrink-0 whitespace-nowrap text-xs sm:text-sm">
						Ngành
					</TabsTrigger>
					<TabsTrigger value="quota" className="shrink-0 whitespace-nowrap text-xs sm:text-sm">
						Chỉ tiêu
					</TabsTrigger>
					{showFacultiesTab && (
						<TabsTrigger
							value="faculties"
							className="shrink-0 whitespace-nowrap text-xs sm:text-sm"
						>
							Khoa
						</TabsTrigger>
					)}
					<TabsTrigger value="provinces" className="shrink-0 whitespace-nowrap text-xs sm:text-sm">
						Tỉnh thành
					</TabsTrigger>
					<TabsTrigger
						value="high-schools"
						className="shrink-0 whitespace-nowrap text-xs sm:text-sm"
					>
						Trường THPT
					</TabsTrigger>
					<TabsTrigger value="approvers" className="shrink-0 whitespace-nowrap text-xs sm:text-sm">
						Người tiếp nhận
					</TabsTrigger>
					<TabsTrigger value="admission" className="shrink-0 whitespace-nowrap text-xs sm:text-sm">
						Diện xét tuyển
					</TabsTrigger>
					{canViewPacing && (
						<TabsTrigger value="pacing" className="shrink-0 whitespace-nowrap text-xs sm:text-sm">
							Nhịp độ
						</TabsTrigger>
					)}
					<TabsTrigger value="hoc-ba" className="shrink-0 whitespace-nowrap text-xs sm:text-sm">
						Học bạ
					</TabsTrigger>
					<TabsTrigger
						value="combinations"
						className="shrink-0 whitespace-nowrap text-xs sm:text-sm"
					>
						Tra tổ hợp
					</TabsTrigger>
					<TabsTrigger
						value="other"
						className="shrink-0 whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-purple-600"
					>
						Khác
					</TabsTrigger>
				</TabsList>
				{/* Chỉ VÙNG NỘI DUNG mờ đi khi người dùng tự yêu cầu nạp lại, và chỉ khi
				    đó. Thanh tab nằm ngoài lớp này: nó là lối đi, không phải dữ liệu, và
				    khoá nó lại là thứ khiến trang có cảm giác chết cứng.

				    Không còn `pointer-events-none` — đổi bộ lọc giữa chừng một lượt fetch
				    đã được `latestFetchId` xử lý đúng (kết quả cũ bị bỏ), nên khoá tương
				    tác chỉ còn là phiền phức chứ không bảo vệ gì.

				    Nhánh "live" không làm mờ gì cả: chỉ báo của nó nằm trên thanh công
				    cụ, và các tab tự nạp thì tự mờ danh sách của mình tại chỗ. */}
				<div
					className={`transition-opacity duration-200 ${
						refreshSource === "user" ? "opacity-60" : ""
					}`}
					aria-busy={isRefreshing}
				>
					<TabsContent value="overview">
						<OverviewTab
							overallStats={overallStats}
							filters={filters}
							onFiltersChange={handleFiltersChange}
						/>
					</TabsContent>

					<TabsContent value="majors">
						<MajorsTab
							view="majors"
							departmentStats={departmentStats}
							facultyStats={facultyStats}
							filters={filters}
							onFiltersChange={handleFiltersChange}
						/>
					</TabsContent>

					<QuotaTab
						filters={filters}
						onFiltersChange={handleFiltersChange}
						refreshToken={refreshToken}
					/>

					{showFacultiesTab && (
						<TabsContent value="faculties">
							<MajorsTab
								view="faculties"
								departmentStats={departmentStats}
								facultyStats={facultyStats}
								filters={filters}
								onFiltersChange={handleFiltersChange}
							/>
						</TabsContent>
					)}

					<TabsContent value="provinces">
						<ProvincesTab
							filters={filters}
							onFiltersChange={handleFiltersChange}
							refreshToken={refreshToken}
						/>
					</TabsContent>

					<TabsContent value="high-schools">
						<HighSchoolsTab
							filters={filters}
							onFiltersChange={handleFiltersChange}
							refreshToken={refreshToken}
						/>
					</TabsContent>

					<TabsContent value="approvers">
						<ApproversTab
							approverStats={approverStats}
							filters={filters}
							onFiltersChange={handleFiltersChange}
						/>
					</TabsContent>

					<TabsContent value="admission">
						<AdmissionTab
							admissionStats={admissionStats}
							filters={filters}
							onFiltersChange={handleFiltersChange}
						/>
					</TabsContent>

					{/* Không render luôn khi thiếu quyền, thay vì chỉ ẩn nút tab: tab đã
					    mount là đã gọi API, và request đó chắc chắn bị server từ chối. */}
					{canViewPacing && (
						<PacingTab
							filters={filters}
							onFiltersChange={handleFiltersChange}
							refreshToken={refreshToken}
						/>
					)}

					<HocBaTab
						filters={filters}
						onFiltersChange={handleFiltersChange}
						refreshToken={refreshToken}
					/>

					{/* Không nhận `filters`/`refreshToken`: bảng mã tổ hợp là dữ liệu quy
					    chế, không phải số liệu thí sinh — nó không đổi theo khoa/khoá và
					    không có gì để "tải lại". */}
					<CombinationLookupTab />

					<TabsContent value="other">
						<OtherStatisticsTab
							filters={filters}
							onFiltersChange={handleFiltersChange}
							refreshToken={refreshToken}
							section={activeSection}
							onSectionChange={handleSectionChange}
						/>
					</TabsContent>
				</div>
			</Tabs>

			<ChangePasswordModal
				isOpen={showChangePassword}
				onClose={() => setShowChangePassword(false)}
			/>
		</div>
	);
}
